import { FunctionComponent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ordersAPI } from '../../../../services/redwolfSdkAPI';
import { format } from 'date-fns';
import { dateFormat } from '../../../../utils/constants';
import Username from '../../Username';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { getProductImage } from '../../../../utils/commonFunctions';
import { setOrderDetails } from '../../../../store/orderSlice';
import { Instagram } from 'react-content-loader';

const OrderDetail: FunctionComponent = () => {
	const location: any = useLocation();
	const { orderDetails } = useSelector((state: any) => state.order);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(false);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [requestRedwolfService] = useRequestRedwolfService();
	const dispatch = useDispatch();

	const transaction = Sentry.startInactiveSpan({ name: 'Order Detail' });

	const products = orderDetails?.lineItems.toArray();

	useEffect(() => {
		if (!orderDetails) {
			setIsSkeletonLoading(true);

			const URLParams = new URLSearchParams(location.search);
			requestRedwolfService(ordersAPI.getOrderById(URLParams.get('orderid')))
				.then((orderResponse: any) => {
					setIsSkeletonLoading(false);

					if (orderResponse.constructor.$metadata$.simpleName === 'Success') {
						dispatch(setOrderDetails(orderResponse.data));
					}
					transaction && transaction.end();
				})
				.catch(() => {
					setIsSkeletonLoading(false);
					transaction && transaction.end();
					showTechnicalErrorMessage();
				});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="myorder-details">
			<div className="account-component subs-components order-details">
				<div className="print-logo">
					<img src="images/vizio-logo-print.svg" alt="logo" />
				</div>

				<Username />

				{isSkeletonLoading ? (
					<Instagram viewBox="0 0 400 130" />
				) : (
					<>
						<div className="oreder-component">
							<div className="order-info">
								<h2 className="order-no">Order # {orderDetails?.orderNumber}</h2>
								<button
									type="submit"
									className="primary-button-small print-btn"
									aria-label="Print Order"
									onClick={() => window.print()}>
									Print Order
								</button>
							</div>

							<div className="order-items">
								{products &&
									products.map((productOrder: any, index: number) => {
										const imagesList = productOrder.images && productOrder.images.toArray();

										return (
											<div className="item" key={index}>
												<div className="item-img">
													<img
														src={getProductImage(imagesList)}
														className="item-img"
														alt={productOrder?.name}
													/>
												</div>

												<div className="item-info">
													<div className="order-item">
														<span className="pr-title">{productOrder && productOrder?.name}</span>
														<span className="item-spare desk">
															{productOrder && productOrder?.sku}
														</span>
													</div>
													<div className="spare__price">
														<span className="item-spar mob"></span>
														<span className="spare-price">
															${productOrder && productOrder?.price}
														</span>
														<span className="spare-qty desk">
															Qty: {productOrder && productOrder?.quantity}
														</span>
													</div>
													<div className="deliver">
														<span className="de-status">{orderDetails?.userState}</span>
													</div>
												</div>
											</div>
										);
									})}
							</div>
						</div>

						<div className="pr-detls-component">
							<div className="order-col">
								<div className="or-details">
									<h3 className="or-dt-tile tile">Order Details</h3>

									<ul className="or-de-list">
										<li className="or-item">
											<span className="or-itTile">Order Date:</span>
											<span className="or-it-info">
												{orderDetails ? format(new Date(orderDetails?.orderDate), dateFormat) : ''}
											</span>
										</li>
										<li className="or-item">
											<span className="or-itTile">Order Subtotal:</span>
											<span className="or-it-info">${orderDetails?.price}</span>
										</li>
										{orderDetails?.tax > 0 && (
											<li className="or-item">
												<span className="or-itTile">Tax:</span>
												<span className="or-it-info">${orderDetails?.tax}</span>
											</li>
										)}
										{orderDetails?.recyclingFee > 0 && (
											<li className="or-item">
												<span className="or-itTile">eRecycle Fee:</span>
												<span className="or-it-info">${orderDetails?.recyclingFee}</span>
											</li>
										)}
										{orderDetails?.shipping > 0 && (
											<li className="or-item">
												<span className="or-itTile">Shipping & Handling:</span>
												<span className="or-it-info">${orderDetails?.shipping}</span>
											</li>
										)}
									</ul>

									<div className="total-price">
										<span className="ttl-title">Total Cost:</span>
										<span className="ttl-price">${orderDetails?.total}</span>
									</div>

									{orderDetails?.savings > 0 && (
										<div className="total-price total-s-price">
											<span className="ttl-title">Total Savings:</span>
											<span className="ttl-price">${orderDetails?.savings}</span>
										</div>
									)}
								</div>
							</div>

							<div className="order-col order-ship-de">
								<div className="or-ship-details">
									<h3 className="or-dt-tile tile">Shipping Address</h3>

									<ul className="or-add">
										<li className="add-list">
											{orderDetails?.shippingAddress?.firstName}{' '}
											{orderDetails?.shippingAddress?.lastName}
										</li>
										<li className="add-list">{orderDetails?.shippingAddress?.streetAddress}</li>
										<li className="add-list">{`${orderDetails?.shippingAddress?.city}, ${orderDetails?.shippingAddress?.state} ${orderDetails?.shippingAddress?.postalCode}`}</li>
										<li className="add-list">{orderDetails?.shippingAddress?.additionalAddress}</li>
										<li className="add-list">{`United States`}</li>
									</ul>
								</div>
								{orderDetails?.cardLastFour && orderDetails?.cardExpirationDate && (
									<div className="or-payment">
										<h3 className="or-dt-tile tile">Payment</h3>

										<div className="or-pay-item">
											<span className="or-pay-icon">
												<img
													src={orderDetails?.cardImageUrl}
													className="or-ic-img"
													alt="payment icon"
												/>
											</span>
											<span className="or-pay-n">{`...${orderDetails?.cardLastFour}`}</span>
											<span className="or-pay-exp">{`EXP.${orderDetails?.cardExpirationDate}`}</span>
										</div>
									</div>
								)}
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default OrderDetail;
